// src/pages/enterprise-ai/PricingPage.js
// T6-PRICING-PANEL-01. Every United markup in one place, in the panel's own look (the Money
// page's header, table, card, dialog and refusal line; the mint tab indicator ExperiencePricing
// uses). Four views:
//   Platform defaults       each source, its rate, its allowed range beside the input, how many
//                           customers override it; Save needs a reason; an out-of-range value is
//                           refused with org-backend's own sentence before anything is sent
//   Per customer            pick an organisation, default and their rate per source, set or clear
//                           an override, and the 30-day re-price and wallet preview before saving
//   History                 every change: when, who, source, organisation, old, new, reason
//   Counts as real revenue  each organisation's switch, its "real since" date, and the line
//                           saying it does not end test mode for mail or money
//
// Calls: mgmt-backend /api/united-pricing/* (pricingPanel.js). Staff only (united_ai admin);
// the sidebar hides the tab otherwise and this page says so if opened by address.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    TextField, MenuItem, Button, CircularProgress, Alert, Autocomplete, Chip, Tabs, Tab, Switch,
    Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import { getAuth } from 'firebase/auth';
import { refusalLine } from '../enterprise/AddEnterpriseDialog';
import { THEME } from './genesisData';
import { usePricingAccess } from '../../hooks/usePricingAccess';
import {
    pricingApi, valueWords, describeValue, rangeWords, toInputValue, checkTyped, hasReason,
    REASON_REQUIRED_LINE, previewLines, REVENUE_SWITCH_LINE, revenueStateWords, historyRows,
} from './pricingPanel';

// ─────────────────────────────────────────────
// STYLED (the Money page's)
// ─────────────────────────────────────────────

const PageContainer = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
});

const Cell = styled(TableCell)({
    padding: '12px 14px',
    fontSize: '0.85rem',
    color: THEME.textPrimary,
    borderBottom: `1px solid ${THEME.border}`,
});

const HeadCell = styled(Cell)({
    color: THEME.mint,
    fontWeight: 600,
});

const fieldSx = {
    InputLabelProps: { sx: { color: THEME.textMuted } },
    InputProps: { sx: { color: THEME.textPrimary, bgcolor: THEME.bgDeep, borderRadius: '8px' } },
};

const primaryBtnSx = {
    bgcolor: THEME.mint, color: '#000', fontWeight: 700, textTransform: 'none', borderRadius: '8px',
    '&:hover': { bgcolor: THEME.mint },
    '&:disabled': { bgcolor: THEME.cardRaised, color: THEME.textMuted },
};

const outlineBtnSx = { color: THEME.mint, borderColor: THEME.mint, textTransform: 'none', fontWeight: 600, borderRadius: '8px' };

const tabsSx = {
    mb: 2,
    borderBottom: `1px solid ${THEME.border}`,
    '& .MuiTab-root': { color: THEME.textMuted, textTransform: 'none', fontWeight: 600, minWidth: 'auto', px: 2 },
    '& .Mui-selected': { color: `${THEME.mint} !important` },
    '& .MuiTabs-indicator': { bgcolor: THEME.mint },
};

const switchSx = {
    '& .MuiSwitch-switchBase.Mui-checked': { color: THEME.mint },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: THEME.mint },
};

const cardSx = { bgcolor: THEME.card, border: `1px solid ${THEME.borderStrong}`, borderRadius: '10px', p: 2 };
const dialogPaper = { sx: { bgcolor: THEME.bg, color: THEME.textPrimary, borderRadius: '12px' } };
const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '—');

function RefusalLine({ text }) {
    if (!text) return null;
    return <Alert severity="error" sx={{ mb: 2, bgcolor: THEME.cardRaised, color: THEME.pink }}>{text}</Alert>;
}

function DoneLine({ lines }) {
    if (!lines || !lines.length) return null;
    return (
        <Alert severity="success" sx={{ mb: 2, bgcolor: THEME.mintDim, color: THEME.textPrimary }}>
            {lines.map((l) => <div key={l}>{l}</div>)}
        </Alert>
    );
}

function Loading() {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: THEME.mint }} /></Box>;
}

function ReasonField({ value, onChange, label = 'Reason for the change (required)' }) {
    return (
        <TextField size="small" label={label} value={value} onChange={(e) => onChange(e.target.value)}
            sx={{ minWidth: 360, flexGrow: 1 }} {...fieldSx} />
    );
}

function RateInput({ row, value, onChange, placeholder, error }) {
    return (
        <TextField size="small" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} error={!!error}
            inputProps={{ inputMode: 'decimal', 'aria-label': row.label + ' rate', style: { textAlign: 'right' } }}
            sx={{ width: 175 }}
            InputProps={{
                ...fieldSx.InputProps,
                endAdornment: <InputAdornment position="end"><Typography sx={{ color: THEME.textMuted, fontSize: '0.8rem' }}>{row.kind === 'multiplier' ? 'x cost' : '%'}</Typography></InputAdornment>,
            }} />
    );
}

const orgLabel = (o) => (o ? (o.name || String(o.orgId)) : '');

// ─────────────────────────────────────────────
// PLATFORM DEFAULTS
// ─────────────────────────────────────────────

function DefaultsView({ sources, onSaved }) {
    const [edits, setEdits] = useState({});
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [done, setDone] = useState(null);

    const changed = sources.filter((s) => edits[s.source] !== undefined && edits[s.source] !== toInputValue(s.kind, s.default));
    const checks = Object.fromEntries(changed.map((s) => [s.source, checkTyped(s, edits[s.source])]));

    const save = async () => {
        setError(null);
        setDone(null);
        if (!hasReason(reason)) { setError(REASON_REQUIRED_LINE); return; }
        const refused = changed.map((s) => checks[s.source].refusal).filter(Boolean);
        if (refused.length) { setError(refused.join('\n')); return; }
        setBusy(true);
        const saved = [];
        try {
            for (const s of changed) {
                const r = await pricingApi.setDefault(s.source, checks[s.source].value, reason.trim());
                saved.push(s.label + ': ' + describeValue(s.kind, r.old) + ' to ' + describeValue(s.kind, r.new));
            }
            setEdits({});
            setReason('');
        } catch (err) {
            setError(refusalLine(err) + (saved.length ? ' (saved before this: ' + saved.length + ')' : ''));
        } finally {
            setBusy(false);
            if (saved.length) { setDone(saved); onSaved(); }
        }
    };

    return (
        <Box>
            <RefusalLine text={error} />
            <DoneLine lines={done} />
            <TableContainer component={Paper} sx={{ bgcolor: THEME.card, borderRadius: '12px', mb: 2 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <HeadCell>Source</HeadCell>
                            <HeadCell>Rate now</HeadCell>
                            <HeadCell>Allowed range</HeadCell>
                            <HeadCell align="right">Customers with an override</HeadCell>
                            <HeadCell>Last change</HeadCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sources.map((s) => {
                            const text = edits[s.source] !== undefined ? edits[s.source] : toInputValue(s.kind, s.default);
                            const c = checks[s.source];
                            return (
                                <TableRow key={s.source} sx={{ '&:hover': { bgcolor: THEME.mintFaint } }}>
                                    <Cell>
                                        <Typography sx={{ color: THEME.textPrimary, fontSize: '0.85rem', fontWeight: 600 }}>{s.label}</Typography>
                                        <Typography sx={{ color: THEME.textFaint, fontSize: '0.7rem' }}>{s.covers}</Typography>
                                    </Cell>
                                    <Cell>
                                        <RateInput row={s} value={text} error={c && c.refusal}
                                            onChange={(v) => { setEdits((e) => ({ ...e, [s.source]: v })); setDone(null); }} />
                                        <Typography sx={{ color: THEME.textMuted, fontSize: '0.7rem', mt: 0.5 }}>{valueWords(s.kind, s.default)}</Typography>
                                    </Cell>
                                    <Cell sx={{ color: THEME.textSecondary }}>{rangeWords(s.kind, s.floor, s.ceiling)}</Cell>
                                    <Cell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{s.overrideCount ?? 0}</Cell>
                                    <Cell sx={{ color: THEME.textSecondary, fontSize: '0.8rem' }}>
                                        {s.updatedAt ? fmtDate(s.updatedAt) : 'Seeded, never changed'}
                                        {s.updatedBy && <Typography sx={{ color: THEME.textFaint, fontSize: '0.7rem' }}>{s.updatedBy}</Typography>}
                                    </Cell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <Typography sx={{ color: THEME.textMuted, fontSize: '0.8rem', mb: 1.5 }}>
                A change applies to new charges only. Money already held and charges already made are never touched.
                {changed.length ? ' ' + changed.length + ' change' + (changed.length === 1 ? '' : 's') + ' not saved yet.' : ''}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <ReasonField value={reason} onChange={setReason} />
                <Button variant="contained" sx={{ ...primaryBtnSx, px: 2.5 }} disabled={busy || !changed.length} onClick={save}
                    startIcon={busy ? <CircularProgress size={16} /> : null}>
                    {busy ? 'Saving...' : 'Save changes'}
                </Button>
                {changed.length > 0 && (
                    <Button onClick={() => { setEdits({}); setError(null); }} disabled={busy} sx={{ color: THEME.textMuted, textTransform: 'none' }}>Undo edits</Button>
                )}
            </Box>
        </Box>
    );
}

// ─────────────────────────────────────────────
// PER CUSTOMER
// ─────────────────────────────────────────────

function PerCustomerView({ orgs, orgsError, initialOrg, onSaved }) {
    const [org, setOrg] = useState(initialOrg || null);
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [edits, setEdits] = useState({}); // source -> typed text, or null to clear the override
    const [preview, setPreview] = useState(null);
    const [previewFor, setPreviewFor] = useState(null);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [done, setDone] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        if (!org) { setDetail(null); return undefined; }
        let live = true;
        setLoading(true);
        setLoadError(null);
        pricingApi.organization(org.orgId)
            .then((d) => { if (live) setDetail(d); })
            .catch((err) => { if (live) { setDetail(null); setLoadError(refusalLine(err)); } })
            .finally(() => { if (live) setLoading(false); });
        return () => { live = false; };
    }, [org, reloadKey]);

    const pick = (o) => { setOrg(o); setEdits({}); setPreview(null); setPreviewFor(null); setError(null); setDone(null); };
    // Any edit after a preview discards it: Save only ever sends what was previewed.
    const edit = (source, v) => { setEdits((e) => ({ ...e, [source]: v })); setPreview(null); setPreviewFor(null); setError(null); setDone(null); };

    const rates = (detail && detail.rates) || [];
    const pending = rates.filter((r) => {
        if (!(r.source in edits)) return false;
        if (edits[r.source] === null) return !!r.override;
        const now = r.override ? toInputValue(r.kind, r.override.value) : '';
        return edits[r.source] !== now && String(edits[r.source]).trim() !== '';
    });
    const checks = Object.fromEntries(pending.filter((r) => edits[r.source] !== null).map((r) => [r.source, checkTyped(r, edits[r.source])]));

    const runPreview = async () => {
        setError(null);
        const refused = Object.values(checks).map((c) => c.refusal).filter(Boolean);
        if (refused.length) { setError(refused.join('\n')); return; }
        // A cleared override is previewed at the platform default it falls back to.
        const proposed = Object.fromEntries(pending.map((r) => [r.source, edits[r.source] === null ? r.default : checks[r.source].value]));
        setBusy(true);
        try {
            const p = await pricingApi.preview(org.orgId, proposed);
            setPreview(p);
            setPreviewFor(proposed);
        } catch (err) {
            setError(refusalLine(err));
        } finally {
            setBusy(false);
        }
    };

    const save = async () => {
        setError(null);
        if (!hasReason(reason)) { setError(REASON_REQUIRED_LINE); return; }
        setBusy(true);
        const saved = [];
        try {
            for (const r of pending) {
                if (edits[r.source] === null) {
                    await pricingApi.clearOverride(org.orgId, r.source, reason.trim());
                    saved.push(r.label + ': override cleared, back on the default');
                } else {
                    const res = await pricingApi.setOverride(org.orgId, r.source, previewFor[r.source], reason.trim());
                    saved.push(r.label + ': ' + (res.old == null ? 'default' : describeValue(r.kind, res.old)) + ' to ' + describeValue(r.kind, res.new));
                }
            }
            setEdits({});
            setReason('');
            setPreview(null);
            setPreviewFor(null);
        } catch (err) {
            setError(refusalLine(err) + (saved.length ? ' (saved before this: ' + saved.length + ')' : ''));
        } finally {
            setBusy(false);
            if (saved.length) { setDone(saved); setReloadKey((k) => k + 1); onSaved(); }
        }
    };

    const lines = previewLines(preview);

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                <Autocomplete
                    sx={{ width: 360 }}
                    size="small"
                    options={orgs}
                    value={org}
                    onChange={(e, v) => pick(v)}
                    getOptionLabel={orgLabel}
                    isOptionEqualToValue={(a, b) => String(a.orgId) === String(b.orgId)}
                    renderInput={(params) => (
                        <TextField {...params} label="Organisation" placeholder="Pick an organisation"
                            InputLabelProps={{ ...params.InputLabelProps, sx: { color: THEME.textMuted } }}
                            InputProps={{ ...params.InputProps, sx: { color: THEME.textPrimary, bgcolor: THEME.card, borderRadius: '8px' } }} />
                    )}
                />
                {detail && detail.revenue && (
                    <Chip size="small" label={revenueStateWords(detail.revenue)}
                        sx={{ bgcolor: detail.revenue.countsAsReal ? THEME.mintDim : THEME.cardRaised, color: detail.revenue.countsAsReal ? THEME.mint : THEME.textSecondary }} />
                )}
            </Box>
            <RefusalLine text={orgsError} />
            <RefusalLine text={loadError} />
            <DoneLine lines={done} />
            {!org ? (
                <Typography sx={{ color: THEME.textMuted, py: 4, textAlign: 'center' }}>Pick an organisation to see its rates.</Typography>
            ) : loading ? <Loading /> : detail && (
                <>
                    <TableContainer component={Paper} sx={{ bgcolor: THEME.card, borderRadius: '12px', mb: 2 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <HeadCell>Source</HeadCell>
                                    <HeadCell>Default</HeadCell>
                                    <HeadCell>Their rate</HeadCell>
                                    <HeadCell>Charged now</HeadCell>
                                    <HeadCell>Allowed range</HeadCell>
                                    <HeadCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rates.map((r) => {
                                    const clearing = edits[r.source] === null;
                                    const text = r.source in edits && !clearing ? edits[r.source] : (r.override ? toInputValue(r.kind, r.override.value) : '');
                                    const c = checks[r.source];
                                    return (
                                        <TableRow key={r.source} sx={{ '&:hover': { bgcolor: THEME.mintFaint } }}>
                                            <Cell sx={{ fontWeight: 600 }}>{r.label}</Cell>
                                            <Cell sx={{ color: THEME.textSecondary }}>{describeValue(r.kind, r.default)}</Cell>
                                            <Cell>
                                                {clearing ? (
                                                    <Typography sx={{ color: THEME.amber, fontSize: '0.85rem' }}>Clearing: back to the default</Typography>
                                                ) : (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <RateInput row={r} value={text} placeholder="uses default" error={c && c.refusal} onChange={(v) => edit(r.source, v)} />
                                                        {r.override && <Chip size="small" label="override" sx={{ bgcolor: THEME.amberDim, color: THEME.amber, fontSize: '0.7rem' }} />}
                                                    </Box>
                                                )}
                                                {r.override && !clearing && (
                                                    <Typography sx={{ color: THEME.textFaint, fontSize: '0.7rem', mt: 0.5 }}>
                                                        {fmtDate(r.override.at)}, {r.override.by}{r.override.reason ? ': ' + r.override.reason : ''}
                                                    </Typography>
                                                )}
                                            </Cell>
                                            <Cell>
                                                {r.effectiveWords || describeValue(r.kind, r.effective)}
                                                <Typography sx={{ color: THEME.textFaint, fontSize: '0.7rem' }}>from {r.from}</Typography>
                                            </Cell>
                                            <Cell sx={{ color: THEME.textSecondary }}>{rangeWords(r.kind, r.floor, r.ceiling)}</Cell>
                                            <Cell align="right">
                                                {r.override && !clearing && (
                                                    <Button size="small" onClick={() => edit(r.source, null)}
                                                        sx={{ color: THEME.pink, border: `1px solid ${THEME.pink}`, textTransform: 'none', fontSize: '0.75rem', px: 1.5 }}>
                                                        Clear
                                                    </Button>
                                                )}
                                                {clearing && (
                                                    <Button size="small" onClick={() => { setEdits((e) => { const n = { ...e }; delete n[r.source]; return n; }); setPreview(null); setPreviewFor(null); }}
                                                        sx={{ color: THEME.textMuted, textTransform: 'none', fontSize: '0.75rem' }}>
                                                        Keep it
                                                    </Button>
                                                )}
                                            </Cell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <RefusalLine text={error} />
                    {lines && (
                        <Box sx={{ ...cardSx, mb: 2 }}>
                            <Typography sx={{ color: THEME.textPrimary, mb: 1 }}>{lines.costLine}</Typography>
                            <Typography sx={{ color: THEME.mint, fontWeight: 700, fontSize: '1.1rem' }}>{lines.walletLine}</Typography>
                            {lines.notes.map((n) => <Typography key={n} sx={{ color: THEME.textMuted, fontSize: '0.8rem', mt: 1 }}>{n}</Typography>)}
                            <Typography sx={{ color: THEME.textFaint, fontSize: '0.75rem', mt: 1 }}>
                                Last {preview.windowDays || 30} days of charges, re-priced at today's rates and at the ones above, divided into the wallet plus travel balance
                                ({describeBalance(preview.balance)}). Nothing has been saved yet.
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                        <ReasonField value={reason} onChange={setReason} />
                        {!preview ? (
                            <Button variant="outlined" sx={{ ...outlineBtnSx, px: 2.5 }} disabled={busy || !pending.length} onClick={runPreview}
                                startIcon={busy ? <CircularProgress size={16} /> : null}>
                                {busy ? 'Checking...' : 'Preview the wallet'}
                            </Button>
                        ) : (
                            <Button variant="contained" sx={{ ...primaryBtnSx, px: 2.5 }} disabled={busy} onClick={save}
                                startIcon={busy ? <CircularProgress size={16} /> : null}>
                                {busy ? 'Saving...' : 'Save override'}
                            </Button>
                        )}
                        {pending.length > 0 && (
                            <Typography sx={{ color: THEME.textMuted, fontSize: '0.8rem' }}>
                                {pending.length} change{pending.length === 1 ? '' : 's'}; preview first, then save.
                            </Typography>
                        )}
                    </Box>
                </>
            )}
        </Box>
    );
}

function describeBalance(b) {
    if (!b) return 'balance unread';
    const c = (x) => (Number.isFinite(x) ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(x / 100) : '—');
    return 'wallet ' + c(b.walletCents) + ', travel ' + c(b.travelCents);
}

// ─────────────────────────────────────────────
// HISTORY
// ─────────────────────────────────────────────

function HistoryView({ sources, orgNames }) {
    const [sourceFilter, setSourceFilter] = useState('');
    const rows = useMemo(() => historyRows(sources).filter((h) => !sourceFilter || h.source === sourceFilter), [sources, sourceFilter]);
    const truncated = sources.filter((s) => (s.historyCount || 0) > (s.history || []).length);

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                <TextField select size="small" label="Source" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}
                    sx={{ width: 260 }} InputLabelProps={{ sx: { color: THEME.textMuted } }}
                    InputProps={{ sx: { color: THEME.textPrimary, bgcolor: THEME.card, borderRadius: '8px' } }}>
                    <MenuItem value="">All sources</MenuItem>
                    {sources.map((s) => <MenuItem key={s.source} value={s.source}>{s.label}</MenuItem>)}
                </TextField>
                <Typography sx={{ color: THEME.textMuted, fontSize: '0.85rem' }}>{rows.length} change{rows.length === 1 ? '' : 's'}, newest first</Typography>
            </Box>
            {truncated.length > 0 && (
                <Alert severity="info" sx={{ mb: 2, bgcolor: THEME.cardRaised, color: THEME.textSecondary }}>
                    Showing the newest {truncated[0].history.length} changes for {truncated.map((s) => s.label + ' (of ' + s.historyCount + ')').join(', ')}.
                </Alert>
            )}
            <TableContainer component={Paper} sx={{ bgcolor: THEME.card, borderRadius: '12px' }}>
                {rows.length === 0 ? (
                    <Typography sx={{ color: THEME.textMuted, textAlign: 'center', py: 6 }}>No changes yet: every rate is still the value it was seeded with.</Typography>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <HeadCell>When</HeadCell>
                                <HeadCell>Who</HeadCell>
                                <HeadCell>Source</HeadCell>
                                <HeadCell>Organisation</HeadCell>
                                <HeadCell>Old</HeadCell>
                                <HeadCell>New</HeadCell>
                                <HeadCell>Reason</HeadCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((h, i) => (
                                <TableRow key={h.source + h.at + i} sx={{ '&:hover': { bgcolor: THEME.mintFaint } }}>
                                    <Cell sx={{ color: THEME.textSecondary, whiteSpace: 'nowrap' }}>{fmtDate(h.at)}</Cell>
                                    <Cell>{h.by || '—'}</Cell>
                                    <Cell>{h.label}</Cell>
                                    <Cell>{h.orgId ? (orgNames[String(h.orgId)] || <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{h.orgId}</span>) : <span style={{ color: THEME.textMuted }}>Platform default</span>}</Cell>
                                    <Cell sx={{ color: THEME.textSecondary }}>{h.old == null ? 'default' : describeValue(h.kind, h.old)}</Cell>
                                    <Cell sx={{ color: THEME.mint }}>{h.new == null ? 'cleared' : describeValue(h.kind, h.new)}</Cell>
                                    <Cell sx={{ color: THEME.textSecondary, maxWidth: 360 }}>{h.reason || '—'}</Cell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>
        </Box>
    );
}

// ─────────────────────────────────────────────
// COUNTS AS REAL REVENUE
// ─────────────────────────────────────────────

function RevenueSwitchDialog({ target, onClose, onDone }) {
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => { setReason(''); setError(null); }, [target]);
    if (!target) return null;
    const { org, on } = target;
    const staff = (() => { try { return getAuth().currentUser?.email || null; } catch (e) { return null; } })();

    const confirm = async () => {
        if (!hasReason(reason)) { setError(REASON_REQUIRED_LINE); return; }
        setBusy(true);
        setError(null);
        try {
            await pricingApi.setRevenueReal(org.orgId, on, reason.trim());
            onDone();
        } catch (err) {
            setError(refusalLine(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" PaperProps={dialogPaper}>
            <DialogTitle sx={{ borderBottom: `1px solid ${THEME.border}`, pb: 2, fontWeight: 700 }}>
                {on ? 'Count ' + orgLabel(org) + ' as real revenue' : 'Stop counting ' + orgLabel(org) + ' as real revenue'}
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
                <Box sx={{ mt: 2 }}>
                    <RefusalLine text={error} />
                    <Typography sx={{ color: THEME.textPrimary, mb: 2 }}>
                        {on
                            ? 'From the moment you confirm, this organisation\'s charges count as real in the 8 AM email and the monthly one. Charges before it stay grey (test).'
                            : 'Every charge from this organisation is counted as test again, and its "real since" date is cleared.'}
                    </Typography>
                    <Typography sx={{ color: THEME.amber, fontSize: '0.85rem', mb: 2 }}>{REVENUE_SWITCH_LINE}</Typography>
                    <TextField fullWidth label="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} {...fieldSx} />
                    <Typography sx={{ color: THEME.textMuted, fontSize: '0.8rem', mt: 1 }}>Changed by {staff || 'your staff session'}</Typography>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 1, borderTop: `1px solid ${THEME.border}` }}>
                <Button onClick={onClose} disabled={busy} sx={{ color: THEME.textMuted, textTransform: 'none' }}>Cancel</Button>
                <Button onClick={confirm} disabled={busy} variant="contained" sx={primaryBtnSx}
                    startIcon={busy ? <CircularProgress size={16} /> : null}>
                    {busy ? 'Saving...' : on ? 'Turn on' : 'Turn off'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function RevenueView({ orgs, orgsError, onChanged }) {
    const [target, setTarget] = useState(null);
    const [done, setDone] = useState(null);
    return (
        <Box>
            <Typography sx={{ color: THEME.textSecondary, fontSize: '0.9rem', mb: 2 }}>{REVENUE_SWITCH_LINE}</Typography>
            <RefusalLine text={orgsError} />
            <DoneLine lines={done} />
            <TableContainer component={Paper} sx={{ bgcolor: THEME.card, borderRadius: '12px' }}>
                {orgs.length === 0 ? (
                    <Typography sx={{ color: THEME.textMuted, textAlign: 'center', py: 6 }}>No organisations to show.</Typography>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <HeadCell>Organisation</HeadCell>
                                <HeadCell>Counts as real revenue</HeadCell>
                                <HeadCell>Since</HeadCell>
                                <HeadCell>Test organisation</HeadCell>
                                <HeadCell align="right">Overrides</HeadCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {orgs.map((o) => {
                                const rev = o.revenue;
                                return (
                                    <TableRow key={o.orgId} sx={{ '&:hover': { bgcolor: THEME.mintFaint } }}>
                                        <Cell>
                                            <Typography sx={{ color: THEME.textPrimary, fontSize: '0.85rem', fontWeight: 600 }}>{orgLabel(o)}</Typography>
                                            <Typography sx={{ color: THEME.textFaint, fontSize: '0.7rem', fontFamily: 'monospace' }}>{o.orgId}</Typography>
                                        </Cell>
                                        <Cell>
                                            {rev ? (
                                                <Switch checked={!!rev.countsAsReal} sx={switchSx}
                                                    inputProps={{ 'aria-label': 'Counts as real revenue for ' + orgLabel(o) }}
                                                    onChange={(e) => { setDone(null); setTarget({ org: o, on: e.target.checked }); }} />
                                            ) : (
                                                <Typography sx={{ color: THEME.pink, fontSize: '0.8rem' }}>
                                                    {o.error ? (o.error.code + ': ' + o.error.message) : 'Switch unread'}
                                                </Typography>
                                            )}
                                        </Cell>
                                        <Cell sx={{ color: rev && rev.countsAsReal ? THEME.mint : THEME.textMuted }}>{revenueStateWords(rev)}</Cell>
                                        <Cell sx={{ color: THEME.textSecondary }}>{rev ? (rev.isTestOrg ? 'Yes: fake drivers, no real mail or money' : 'No') : '—'}</Cell>
                                        <Cell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{o.overrideCount ?? 0}</Cell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>
            <RevenueSwitchDialog
                target={target}
                onClose={() => setTarget(null)}
                onDone={() => {
                    setDone([orgLabel(target.org) + (target.on ? ' now counts as real revenue.' : ' is counted as test again.')]);
                    setTarget(null);
                    onChanged();
                }}
            />
        </Box>
    );
}

// ─────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────

const VIEWS = [
    { value: 'defaults', label: 'Platform defaults' },
    { value: 'customer', label: 'Per customer' },
    { value: 'history', label: 'History' },
    { value: 'revenue', label: 'Counts as real revenue' },
];

export default function PricingPage() {
    const access = usePricingAccess();
    const [view, setView] = useState('defaults');
    const [sources, setSources] = useState([]);
    const [sourcesError, setSourcesError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [orgs, setOrgs] = useState([]);
    const [orgsError, setOrgsError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [orgReloadKey, setOrgReloadKey] = useState(0);

    useEffect(() => {
        if (access !== 'allowed') return undefined;
        let live = true;
        setLoading(true);
        pricingApi.sources()
            .then((d) => { if (live) { setSources(d.sources || []); setSourcesError(null); } })
            .catch((err) => {
                console.error('[Pricing] sources unread', err.response?.status, err.response?.data || err.message);
                if (live) setSourcesError(refusalLine(err));
            })
            .finally(() => { if (live) setLoading(false); });
        return () => { live = false; };
    }, [access, reloadKey]);

    useEffect(() => {
        if (access !== 'allowed') return undefined;
        let live = true;
        pricingApi.organizations()
            .then((d) => {
                if (!live) return;
                const rows = [...(d.organizations || [])].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
                setOrgs(rows);
                setOrgsError(null);
            })
            .catch((err) => {
                console.error('[Pricing] organisations unread', err.response?.status, err.response?.data || err.message);
                if (live) setOrgsError('The organisation list could not be read. ' + refusalLine(err));
            });
        return () => { live = false; };
    }, [access, orgReloadKey]);

    const orgNames = useMemo(() => Object.fromEntries(orgs.map((o) => [String(o.orgId), o.name || String(o.orgId)])), [orgs]);
    const reloadAll = useCallback(() => { setReloadKey((k) => k + 1); setOrgReloadKey((k) => k + 1); }, []);

    const header = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <PriceChangeIcon sx={{ color: THEME.mint, fontSize: 30 }} />
            <Box>
                <Typography sx={{ fontSize: '1.8rem', color: THEME.textPrimary, fontWeight: 'bold' }}>Pricing</Typography>
                <Typography sx={{ color: THEME.textMuted, fontSize: '0.9rem' }}>Every United markup in one place, an override per customer, and the real revenue switch</Typography>
                <Typography sx={{ color: THEME.textMuted, fontSize: '0.8rem', fontStyle: 'italic' }}>Staff only; customers never see this. A change applies to new charges only.</Typography>
            </Box>
        </Box>
    );

    if (access !== 'allowed') {
        return (
            <PageContainer>
                {header}
                {access === 'checking' ? <Loading /> : (
                    <Alert severity="warning" sx={{ bgcolor: THEME.amberDim, color: THEME.textPrimary }}>
                        Pricing is for staff with United AI admin. Your sign-in does not have it, or the pricing service did not answer.
                    </Alert>
                )}
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            {header}
            <Tabs value={view} onChange={(e, v) => setView(v)} sx={tabsSx}>
                {VIEWS.map((v) => <Tab key={v.value} value={v.value} label={v.label} />)}
            </Tabs>
            {sourcesError && (view === 'defaults' || view === 'history') ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 6 }}>
                    <Alert severity="error" sx={{ bgcolor: THEME.cardRaised, color: THEME.pink }}>{sourcesError}</Alert>
                    <Button variant="outlined" onClick={() => setReloadKey((k) => k + 1)} sx={outlineBtnSx}>Retry</Button>
                </Box>
            ) : loading && (view === 'defaults' || view === 'history') ? <Loading /> : (
                <>
                    {view === 'defaults' && <DefaultsView sources={sources} onSaved={reloadAll} />}
                    {view === 'customer' && <PerCustomerView orgs={orgs} orgsError={orgsError} onSaved={reloadAll} />}
                    {view === 'history' && <HistoryView sources={sources} orgNames={orgNames} />}
                    {view === 'revenue' && <RevenueView orgs={orgs} orgsError={orgsError} onChanged={() => setOrgReloadKey((k) => k + 1)} />}
                </>
            )}
        </PageContainer>
    );
}
